import React, { useEffect, useCallback } from 'react';
import { TLanguageIDs, IItemsOfGroupsOfMediaSettings } from 'types';
import useEmblaCarousel from 'embla-carousel-react';
import { CarouselImgItem } from './components/CarouselImgItem';
import CarouselVideoItem from './components/CarouselVideoItem';
import { ThumbImgItem } from './components/ThumbImgItem';
import ThumbVideoItem from './components/ThumbVideoItem';
import style from './EmblaCarouselForInfo.module.scss';

interface IEmblaCarouselForInfo {
	onSetActualPageOfCarousel: (page: number) => void;
	items: IItemsOfGroupsOfMediaSettings[];
	languageId: null | TLanguageIDs;
	selectedIndex: number;
	selectedStyleLanguage: TLanguageIDs | null;
}

const EmblaCarouselForInfo: React.FC<IEmblaCarouselForInfo> = ({
	onSetActualPageOfCarousel,
	items,
	languageId,
	selectedIndex,
	selectedStyleLanguage,
}) => {
	const [mainViewportRef, embla] = useEmblaCarousel({ skipSnaps: false });
	const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
		containScroll: 'keepSnaps',
		dragFree: true,
	});

	const onThumbClick = useCallback(
		(index) => {
			if (!embla || !emblaThumbs) {
				return;
			}
			if (emblaThumbs.clickAllowed()) {
				embla.scrollTo(index);
			}
		},
		[embla, emblaThumbs],
	);

	const onSelect = useCallback(() => {
		if (!embla || !emblaThumbs) {
			return;
		}
		emblaThumbs.scrollTo(embla.selectedScrollSnap());
		onSetActualPageOfCarousel(embla.selectedScrollSnap());
	}, [embla, emblaThumbs]);

	useEffect(() => {
		if (!embla) {
			return;
		}
		onSelect();
		embla.on('select', onSelect);
	}, [embla, onSelect]);

	return (
		<>
			<div className={style.embla}>
				<div className={style.emblaViewport} ref={mainViewportRef}>
					<div className={style.emblaContainer}>
						{items
							.filter((el) => el.mediaFileType === 1 || el.mediaFileType === 2)
							.map((el) => (
								<div className={style.emblaSlide} key={el.translationId}>
									<div className={style.emblaSlideInner}>
										{el.mediaFileType === 1 ? (
											<CarouselImgItem
												translationId={el.translationId}
												isMakeImageRequest={selectedStyleLanguage === languageId}
											/>
										) : null}
										{el.mediaFileType === 2 ? (
											<CarouselVideoItem
												translationId={el.translationId}
												isMakeImageRequest={selectedStyleLanguage === languageId}
											/>
										) : null}
									</div>
								</div>
							))}
					</div>
				</div>
			</div>

			<div className={`${style.embla} ${style.emblaThumb}`}>
				<div className={style.emblaViewport} ref={thumbViewportRef}>
					<div className={style.emblaContainer}>
						{items
							.filter((el) => el.mediaFileType === 1 || el.mediaFileType === 2)
							.map((el, index: number) => (
								<div className={style.thumbWrapper} key={el.translationId}>
									{el.mediaFileType === 1 ? (
										<ThumbImgItem
											onClick={() => onThumbClick(index)}
											selected={index === selectedIndex}
											isMakeRequest={selectedStyleLanguage === languageId}
											translationId={el.translationId}
											filePath={el.filePath}
										/>
									) : null}
									{el.mediaFileType === 2 ? (
										<ThumbVideoItem
											onClick={() => onThumbClick(index)}
											selected={index === selectedIndex}
											isMakeRequest={selectedStyleLanguage === languageId}
											translationId={el.translationId}
											filePath={el.filePath}
										/>
									) : null}
								</div>
							))}
					</div>
				</div>
			</div>
		</>
	);
};

export default React.memo(EmblaCarouselForInfo);
